
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Theme, { HierarchicalTheme } from '../repositories/data/Theme';
import SearchThemeSelectorElement from './SearchThemeSelectorElement.vue';

interface ThemeToggle {
    id: string | null;
    hasChildren: boolean;
}

const THEME_FILTER_DEBOUNCE_MS = 300;

@Component({
    components: {
        SearchThemeSelectorElement,
    },
})
export default class SearchThemeSelector extends Vue {
    public themeSettingInput: string | null = null;
    public themeListFilter: string = '';
    public themeListFilterTimeout: number | null = null;

    @Prop({ type: Boolean, default: true }) public readonly isLoading!: boolean;
    @Prop({ type: Array, default: () => [] }) public readonly themes!: Theme[];
    @Prop({ type: String, default: '' }) public readonly fieldPrefix!: string;
    @Prop({ type: String, default: null }) public readonly lastValue!: string | null;
    @Prop({ type: Boolean, default: false }) public readonly isHomepageButton!: boolean;

    public get resolvedButtonClass(): string {
        if (this.isHomepageButton) {
            return 'button ow-control-h is-primary ow-splash-theme-selector-button';
        }
        return 'button ow-control-h is-text ow-search-settings-button';
    }

    public get hierarchicalThemes(): HierarchicalTheme[] {
        return this.themes
            .filter(t => t.parent === null)
            .map(theme => ({
                ...theme,
                searchTerms: theme.label
                    ? theme.label
                          .toLowerCase()
                          .split(/\s+/)
                          .filter(word => word.length > 0)
                    : [],
                children: this.themes
                    .filter(t => t.parent && t.parent.id === theme.id)
                    .map(childTheme => ({
                        ...childTheme,
                        searchTerms: childTheme.label
                            ? childTheme.label
                                  .toLowerCase()
                                  .split(/\s+/)
                                  .filter(word => word.length > 0)
                            : [],
                        children: [],
                    })),
            }));
    }

    public get currentThemeLabel(): string {
        const currentTheme = this.themes.find(theme => theme.id === this.themeSettingInput);
        return currentTheme && currentTheme.label
            ? (currentTheme.parent && currentTheme.parent.label ? currentTheme.parent.label + ': ' : '') +
                  currentTheme.label
            : this.isHomepageButton
              ? this.$t('search.by-theme').toString()
              : this.$t('theme.themes').toString();
    }

    public get fieldName(): string {
        return 'ow-search-setting-' + this.fieldPrefix + '-theme';
    }

    @Watch('themeSettingInput')
    public onChangeThemeSetting(themeId: string | null, oldThemeId: string | null) {
        if (themeId !== oldThemeId) {
            this.$emit('theme-changed', themeId);
        }
        this.$nextTick(() => {
            const themeDropdown = this.$refs.themeDropdown as HTMLElement;
            const activeEntry = themeDropdown?.querySelector(':focus') as HTMLElement;
            activeEntry?.blur();
        });
    }

    @Watch('lastValue', { immediate: true })
    public onLastValueChanged(value: string | null) {
        this.themeSettingInput = value;
    }

    public selectTheme(id: string | null) {
        this.themeSettingInput = id;
    }

    public onThemeListFilterInput(event: any) {
        const value = event.target.value || '';

        // Debounced so typing stays responsive
        if (this.themeListFilterTimeout !== null) {
            window.clearTimeout(this.themeListFilterTimeout);
        }

        this.themeListFilterTimeout = window.setTimeout(() => {
            this.themeListFilterTimeout = null;
            this.themeListFilter = value;
        }, THEME_FILTER_DEBOUNCE_MS);
    }

    public beforeDestroy() {
        if (this.themeListFilterTimeout !== null) {
            window.clearTimeout(this.themeListFilterTimeout);
        }
    }
}
